import {useMemo} from 'react';

import {useGlobalFulfillmentDetail} from '@/components/global/providers/GlobalFulfillmentDetailProvider';
import {
  type EventBarSearchHistoryQuery,
  type FulfillmentDetailStrategy,
  useEventBarSearchHistoryQuery,
} from '@/graphql/types';
import useIdentity from '@/hooks/useIdentity';
import {DateTimeFormats, formatEventBarDate} from '@/utils/dateFormat';
import RequiredPath from '@/utils/RequiredPath';
import {formatFulfillmentLocation, isPastEvent} from './helpers/eventBarRecentUtils';

type SearchHistoryNode = RequiredPath<
  EventBarSearchHistoryQuery,
  ['identity', 'fulfillmentDetails', 'edges', number, 'node']
>;
export type SearchHistoryAddress = SearchHistoryNode['address'];

export type EventBarSearchHistoryEntry = {
  fulfillmentDetailId: string;
  addressLabel: string;
  dateLabel: string;
  timeLabel: string;
  headcount: number | null;
  address: SearchHistoryAddress;
  strategy: FulfillmentDetailStrategy;
  eventName: string | null;
  eventOn: string | null;
  eventLocalTime: string | null;
};

const toEntry = (node: SearchHistoryNode): EventBarSearchHistoryEntry => ({
  fulfillmentDetailId: node.id,
  addressLabel: formatFulfillmentLocation(node.address),
  dateLabel: formatEventBarDate(node.eventAt, node.address.timeZoneIdentifier),
  // Only surface a time when the detail actually has one (dateLabel/eventAt alone would render midnight).
  timeLabel: node.eventLocalTime
    ? formatEventBarDate(
        node.eventAt,
        node.address.timeZoneIdentifier,
        DateTimeFormats.HourWithMinutes,
      )
    : '',
  headcount: node.headcount ?? null,
  address: node.address,
  strategy: node.strategy,
  eventName: node.eventName ?? null,
  eventOn: node.eventOn ?? null,
  eventLocalTime: node.eventLocalTime ?? null,
});

/**
 * Flattens the user's recent fulfillment details into the search-history cells shown in the event bar.
 *
 * We filter out `currentFulfillmentDetailId` as it is the search the user is actively editing in the bar.
 */
export const toEventBarSearchHistory = (
  data: EventBarSearchHistoryQuery | undefined,
  now = Date.now(),
  currentFulfillmentDetailId?: string | null,
): EventBarSearchHistoryEntry[] =>
  (data?.identity?.fulfillmentDetails?.edges ?? []).flatMap(edge => {
    const node = edge.node;
    if (!node || isPastEvent(node.eventAt, now)) return [];
    if (node.address.latitude == null || node.address.longitude == null) return [];
    if (currentFulfillmentDetailId && node.id === currentFulfillmentDetailId) return [];
    return [toEntry(node)];
  });

const useEventBarSearchHistory = (): EventBarSearchHistoryEntry[] => {
  const {isLoggedIn} = useIdentity();
  const {fulfillmentDetail} = useGlobalFulfillmentDetail();
  const {data} = useEventBarSearchHistoryQuery({skip: !isLoggedIn});
  const currentFulfillmentDetailId = fulfillmentDetail?.id ?? null;

  return useMemo(
    () => (isLoggedIn ? toEventBarSearchHistory(data, undefined, currentFulfillmentDetailId) : []),
    [isLoggedIn, data, currentFulfillmentDetailId],
  );
};

export default useEventBarSearchHistory;
